import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MediaType } from "../../types";

export function MediaGallery({ media, isSkeleton }: { media: { url: string; type: MediaType }[]; isSkeleton?: boolean; }) {
    const galleryRef = useRef<HTMLDivElement | null>(null);
    const innerGalleryRef = useRef<HTMLDivElement | null>(null);
    const innerItemsRef = useRef<(HTMLDivElement | null)[]>([]);
    const mediaContainerRef = useRef<HTMLDivElement | null>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);

    const [overlayIsActive, setOverlayIsActive] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);

    const startX = useRef(0);
    const currentX = useRef(0);
    const dragging = useRef(false);

    useEffect(() => {
        if(!overlayIsActive) {
            if(videoRef.current) {
                videoRef.current.pause();
                videoRef.current.currentTime = 0;
            }
            return;
        }

        if(videoRef.current) {
            videoRef.current.pause();
        }

    }, [overlayIsActive, selectedIndex]);

    useEffect(() => {
        if(!overlayIsActive) return;
        const el = innerItemsRef.current[selectedIndex];
        if(!el) return;
        el.scrollIntoView({
            behavior: "smooth",
            inline: "center",
            block: "nearest",
        });
    }, [selectedIndex, overlayIsActive]);

    useEffect(() => {
        if(!overlayIsActive) return;
        const handleKey = (e: KeyboardEvent) => {
            if(e.key === "ArrowRight") {
                setSelectedIndex((p) => (p + 1) % media.length);
            } else if(e.key === "ArrowLeft") {
                setSelectedIndex((p) => (p - 1 + media.length) % media.length);
            }
        };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [overlayIsActive, media.length]);

    const useHorizontalWheelScroll = (ref: React.RefObject<HTMLDivElement | null>) => {
        useEffect(() => {
            const el = ref.current;
            if(!el) return;

            let velocity = 0;
            let rafId: number | null = null;
            const handleWheel = (e: WheelEvent) => {
                e.preventDefault();
                velocity += e.deltaY * 0.15;
                if(!rafId) rafId = requestAnimationFrame(animateScroll);
            };
            const animateScroll = () => {
                el.scrollLeft += velocity;
                velocity *= 0.88;
                if(Math.abs(velocity) > 0.1) {
                    rafId = requestAnimationFrame(animateScroll);
                } else {
                    rafId = null;
                }
            };

            el.addEventListener("wheel", handleWheel, { passive: false });
            return () => {
                el.removeEventListener("wheel", handleWheel);
                if(rafId) cancelAnimationFrame(rafId);
            };
        }, [ref]);
    };
    useHorizontalWheelScroll(galleryRef);
    useHorizontalWheelScroll(innerGalleryRef);

    const handleTouchStart = (e: React.TouchEvent) => {
        dragging.current = true;
        startX.current = e.touches[0].clientX;
    };
    const handleTouchMove = (e: React.TouchEvent) => {
        if(!dragging.current) return;
        currentX.current = e.touches[0].clientX;
        const diff = (currentX.current - startX.current) / 3;
        if(mediaContainerRef.current) {
            mediaContainerRef.current.style.transform = `translateX(${diff}px)`;
        }
    };
    const handleTouchEnd = () => {
        if(!dragging.current) return;
        const diff = currentX.current - startX.current;
        const threshold = 10;
        const sensitivity = 70;
        if(mediaContainerRef.current) {
            mediaContainerRef.current.style.transition = "transform 0.25s ease";
            mediaContainerRef.current.style.transform = "translateX(0px)";
        }
        if(Math.abs(Math.abs(diff) - sensitivity) > threshold && Math.abs(diff) > sensitivity) {
            if(diff < 0) {
                setSelectedIndex((p) => (p + 1) % media.length);
            } else {
                setSelectedIndex((p) => (p - 1 + media.length) % media.length);
            }
        }
        setTimeout(() => {
            if(mediaContainerRef.current) {
                mediaContainerRef.current.style.transition = "";
            }
        }, 250);
        dragging.current = false;
        startX.current = 0;
        currentX.current = 0;
    };

    const openOverlay = (index: number) => {
        setSelectedIndex(index);
        setOverlayIsActive(true);
    };
    const closeOverlay = () => {
        setOverlayIsActive(false);
    };

    const currentMedia = media[selectedIndex];

    if(isSkeleton) {
        return (
            <div className="media-gallery">
                {Array.from({ length: 4 }).map((_, i) => (
                    <MediaItem key={`media-item-skeleton_${i}`} item={{ isSkeleton: true }} />
                ))}
            </div>
        );
    }

    return (
        <>
            <div className="media-gallery" ref={galleryRef}>
                {media.map((m, i) => (
                    <MediaItem key={i} item={m} onClick={() => openOverlay(i)} />
                ))}
            </div>

            {createPortal(
                <div
                    className={`media-overlay ${overlayIsActive ? "active" : ""}`}
                    onClick={(e) => {
                        if(e.target === e.currentTarget) closeOverlay();
                    }}
                >
                    <div
                        className="media-gallery__main-content"
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onClick={(e) => {
                            if(e.target === e.currentTarget) closeOverlay();
                        }}
                    >
                        {media.length > 1 && (
                            <button
                                className="gallery-navigate__btn"
                                onClick={() => setSelectedIndex((p) => (p - 1 + media.length) % media.length)}
                            >
                                <ion-icon name="chevron-back" />
                            </button>
                        )}

                        <div ref={mediaContainerRef} className="media-swipe-container">
                            {currentMedia?.type === MediaType.IMAGE ? (
                                <img src={currentMedia.url} className="expanded-media" alt="" />
                            ) : (
                                <video
                                    ref={videoRef}
                                    src={currentMedia.url}
                                    className="expanded-media"
                                    controls
                                />
                            )}
                        </div>

                        {media.length > 1 && (
                            <button
                                className="gallery-navigate__btn"
                                onClick={() => setSelectedIndex((p) => (p + 1) % media.length)}
                            >
                                <ion-icon name="chevron-forward" />
                            </button>
                        )}
                    </div>

                    <div className="media-gallery--wrapper">
                        <div className="media-gallery" ref={innerGalleryRef}>
                            {media.map((m, i) => (
                                <div
                                    key={i}
                                    ref={(el) => {
                                        innerItemsRef.current[i] = el;
                                    }}
                                >
                                    <MediaItem item={m} onClick={() => setSelectedIndex(i)} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}

function MediaItem({
    item,
    onClick,
}: {
    item: { url: string; type: MediaType } | { isSkeleton: true };
    onClick?: () => void;
}) {
    if('isSkeleton' in item) {
        return (
            <div className="skeleton-block media"></div>
        );
    }

    if(item.type === MediaType.IMAGE) {
        return <img src={item.url} className="media" onClick={onClick} alt="" />;
    }

    return (
        <div className="media-video" onClick={onClick}>
            <video src={item.url} className="media" muted preload="metadata" />
            <div className="media-video__play">
                <div className="media-play__btn">
                    <ion-icon name="play" />
                </div>
            </div>
        </div>
    );
}